import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Link2, UserPlus, Inbox, ChevronDown } from "lucide-react";
import { useCustomers, nameOf } from "@/lib/lookups";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";
const empty = { external_tenant_id: "", external_customer_id: "", product_instance_id: "", navscora_customer_id: "", notes: "" };

export function MappingsManager({ open, onOpenChange, integration }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [sel, setSel] = useState({});          // tenant_id -> chosen customer id
  const [showManual, setShowManual] = useState(false);
  const { data: customers } = useCustomers();
  const id = integration?.id;

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["pending-tenants", id] });
    qc.invalidateQueries({ queryKey: ["mappings", id] });
    qc.invalidateQueries({ queryKey: ["integrations"] });
    qc.invalidateQueries({ queryKey: ["customers"] });
  };

  const { data: pending, isLoading: pendingLoading } = useQuery({
    queryKey: ["pending-tenants", id],
    enabled: !!id && open,
    queryFn: async () => (await api.get(`/integrations/${id}/pending-tenants`)).data,
  });
  const { data: mappings, isLoading } = useQuery({
    queryKey: ["mappings", id],
    enabled: !!id && open,
    queryFn: async () => (await api.get(`/integrations/${id}/mappings`)).data,
  });

  const link = useMutation({
    mutationFn: async ({ tenantId, payload }) =>
      (await api.post(`/integrations/${id}/pending-tenants/${encodeURIComponent(tenantId)}/link`, payload)).data,
    onSuccess: (d) => { invalidateAll(); toast.success(`Linked — ${d.resolved_usage_count} usage record(s) now count in finance`); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const add = useMutation({
    mutationFn: async () => (await api.post(`/integrations/${id}/mappings`, { ...form, active: true })).data,
    onSuccess: () => { invalidateAll(); setForm(empty); toast.success("Mapping added"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (mid) => api.delete(`/integrations/mappings/${mid}`),
    onSuccess: () => { invalidateAll(); toast.success("Mapping removed"); },
  });

  if (!integration) return null;
  const canSubmit = form.external_tenant_id.trim() && form.navscora_customer_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="mappings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="h-4 w-4 text-cyan-400" /> {integration.name} — Customer Mappings</DialogTitle>
        </DialogHeader>

        {/* ---- Pending tenants review queue ---- */}
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3" data-testid="pending-tenants-section">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-200">
            <Inbox className="h-4 w-4" /> Tenants awaiting mapping
            <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-200" data-testid="pending-count">{pending?.length || 0}</span>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            These tenants sent production usage but aren't linked yet, so their data is held out of finance. Link each to a NavScora customer to resolve it instantly.
          </p>

          {pendingLoading ? (
            <div className="flex h-16 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400" /></div>
          ) : !pending?.length ? (
            <p className="py-4 text-center text-sm text-slate-500" data-testid="pending-empty">Nothing waiting — all tenants that sent usage are mapped. 🎉</p>
          ) : (
            <div className="mt-3 space-y-2" data-testid="pending-list">
              {pending.map((t) => (
                <div key={t.tenant_id} className="rounded-lg border border-[#1E2D4A] bg-[#0B1120] p-3" data-testid={`pending-row-${t.tenant_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-100">{t.tenant_name || t.tenant_id}</div>
                      <div className="text-[11px] text-slate-500">
                        <span className="font-mono-fin text-cyan-300">{t.tenant_id}</span>
                        {t.latest_period ? ` · ${t.latest_period}` : ""}{t.period_count > 1 ? ` (+${t.period_count - 1} more)` : ""}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(t.metrics || {}).slice(0, 3).map(([k, v]) => (
                        <span key={k} className="rounded border border-[#1E2D4A] bg-[#17233B] px-1.5 py-0.5 text-[10px] text-slate-400">
                          {k}: <span className="text-slate-200">{Number.isInteger(v) ? v : Number(v).toLocaleString()}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Select value={sel[t.tenant_id] || ""} onValueChange={(v) => setSel({ ...sel, [t.tenant_id]: v })}>
                      <SelectTrigger className={`h-8 flex-1 min-w-[160px] text-xs ${inputCls}`} data-testid={`pending-customer-select-${t.tenant_id}`}><SelectValue placeholder="Link to existing customer…" /></SelectTrigger>
                      <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                        {(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 bg-gradient-to-r from-cyan-500 to-sky-600 text-xs font-semibold text-slate-900"
                      disabled={!sel[t.tenant_id] || link.isPending}
                      onClick={() => link.mutate({ tenantId: t.tenant_id, payload: { navscora_customer_id: sel[t.tenant_id] } })}
                      data-testid={`pending-link-${t.tenant_id}`}>
                      <Link2 className="mr-1 h-3.5 w-3.5" /> Link
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 border-emerald-500/30 bg-transparent text-xs text-emerald-300 hover:bg-emerald-500/10"
                      disabled={link.isPending}
                      onClick={() => link.mutate({ tenantId: t.tenant_id, payload: { create_customer: true, customer_name: t.tenant_name || t.tenant_id } })}
                      data-testid={`pending-create-${t.tenant_id}`}>
                      <UserPlus className="mr-1 h-3.5 w-3.5" /> Create &amp; link
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- Active mappings ---- */}
        <div className="mt-4">
          <div className="mb-2 text-sm font-semibold text-slate-200">Active mappings</div>
          {isLoading ? (
            <div className="flex h-16 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>
          ) : !mappings?.length ? (
            <p className="py-4 text-center text-sm text-slate-500" data-testid="mappings-empty">No mappings yet.</p>
          ) : (
            <div className="space-y-2" data-testid="mappings-list">
              {mappings.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg border border-[#1E2D4A] bg-[#0B1120] px-3 py-2.5" data-testid={`mapping-row-${m.id}`}>
                  <div className="text-xs">
                    <div className="text-slate-200"><span className="font-mono-fin text-cyan-300">{m.external_tenant_id}</span>{m.external_customer_id ? <span className="text-slate-500"> / {m.external_customer_id}</span> : null} → <span className="text-emerald-300">{nameOf(customers, m.navscora_customer_id)}</span></div>
                    <div className="text-[10px] text-slate-500">{m.product_instance_id ? `Instance ${m.product_instance_id} · ` : ""}Added {fmtDate(m.created_at)}</div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400" onClick={() => del.mutate(m.id)} data-testid={`mapping-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- Manual add (fallback) ---- */}
        <div className="mt-3 border-t border-[#1E2D4A] pt-2">
          <button className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200" onClick={() => setShowManual((s) => !s)} data-testid="toggle-manual-mapping">
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showManual ? "rotate-180" : ""}`} /> Add a mapping manually
          </button>
          {showManual && (
            <div className="mt-2 grid grid-cols-1 gap-3 rounded-lg border border-[#1E2D4A] bg-[#17233B]/50 p-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-slate-400">External Tenant ID *</Label>
                <Input value={form.external_tenant_id} onChange={(e) => setForm({ ...form, external_tenant_id: e.target.value })} placeholder="acme" className={`mt-1 ${inputCls}`} data-testid="mapping-tenant-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">External Customer ID</Label>
                <Input value={form.external_customer_id} onChange={(e) => setForm({ ...form, external_customer_id: e.target.value })} placeholder="cus_123 (optional)" className={`mt-1 ${inputCls}`} data-testid="mapping-extcust-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">NavScora Customer *</Label>
                <Select value={form.navscora_customer_id} onValueChange={(v) => setForm({ ...form, navscora_customer_id: v })}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="mapping-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                    {(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end justify-end">
                <Button onClick={() => add.mutate()} disabled={!canSubmit || add.isPending} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="mapping-add-button">
                  {add.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Add Mapping
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
